// ─────────────────────────────────────────────────────────────────────────────
// FEEDBACK CHIPS → THE INTERESTS SURFACE (Ticket 6 #5).
//
//   POST <runtime>/app/interests    Authorization: Bearer <supabase jwt>
//   body: {patch: {topics: [{label, delta}]}, note}
//   → the updated profile
//
// This is the Ticket-2 owner-private interests surface, unchanged. A chip press
// is an OWNER-AUTHORED edit to the fan's own profile — so the patch is written
// with `source: 'explicit'` (the runtime's default), which the write-side
// invariant treats as authored: stating supersedes observing, and a later
// inference pass may not silently overwrite it.
//
// The write lands in the fan's DO. The next `/app/rank-candidates` call reads
// `mem:interests` fresh, so the reorder shows up on the next refresh with no
// client-side re-sorting — the ordering authority stays in one place.
//
// The show is not involved in, and never learns of, a chip press.
// ─────────────────────────────────────────────────────────────────────────────

import {INTERESTS_ENDPOINT} from './config'
import {assertRuntimeOrigin} from './showFence'

/** How hard one chip press moves a topic's weight. */
export const CHIP_DELTA = 0.35

export type ChipDirection = 'more' | 'less'

export interface TopicPatchEntry {
  label: string
  delta?: number
  weight?: number
  remove?: boolean
  source?: 'explicit' | 'inferred'
}

export interface InterestPatch {
  topics?: TopicPatchEntry[]
  authors?: Array<{did?: string; handle?: string; weight?: number; delta?: number; remove?: boolean}>
  /**
   * Mutes are `{pattern}`, NOT `{label}` — the scorer matches a mute pattern as
   * a case-insensitive substring of the card text, or as an exact author
   * did/handle. They are HARD FILTERS applied before scoring (hide-not-rank),
   * which is why a muted card is absent from `order` rather than last in it.
   */
  mutes?: Array<{pattern: string; reason?: string; remove?: boolean}>
}

/**
 * Build the patch for a chip press over a card's topics.
 *
 * "More like this" bumps every topic the card carries; "less like this" drops
 * them by the same magnitude. Symmetric on purpose — a fan who over-corrects
 * can undo it with the opposite chip, which a mute could not.
 */
export function chipPatch(topics: string[], direction: ChipDirection): InterestPatch {
  const delta = direction === 'more' ? CHIP_DELTA : -CHIP_DELTA
  return {
    topics: topics
      .filter(t => typeof t === 'string' && t.trim())
      .map(label => ({label: label.trim(), delta})),
  }
}

export class InterestsError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'InterestsError'
    this.status = status
  }
}

/** Apply an interest patch to the fan's own profile. */
export async function patchInterests(
  patch: InterestPatch,
  token: string,
  {note}: {note?: string} = {},
): Promise<void> {
  assertRuntimeOrigin(INTERESTS_ENDPOINT)

  const res = await fetch(INTERESTS_ENDPOINT, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({patch, ...(note ? {note} : {})}),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as Record<string, unknown>)
    const detail = typeof body?.error === 'string' ? body.error : `interests write failed (${res.status})`
    throw new InterestsError(detail, res.status)
  }
}
